import { Fragment, useMemo } from 'react'
import useMozgotekaClasses from './hooks/useMozgotekaClasses'
import useMozgotekaContext from './hooks/useMozgotekaContext'

type MozgotekaProps = {
  as?: React.ElementType
  children?: React.ReactNode
  className?: string
  container?: {
    as: React.ElementType
    className?: string
  }
  safe?: boolean
  signet?: boolean
  variant?: 'blue' | 'yellow' | 'green' | 'pink'
  revert?: boolean
}

const Mozgoteka = (props: MozgotekaProps) => {
  const {
    as: Component,
    children,
    container,
    safe = true,
    signet,
    variant = 'blue',
    revert = false,
    ...rest
  } = props ?? {}

  const context = useMozgotekaContext(Mozgoteka.name, false)
  const mozgotekaClasses = useMozgotekaClasses({ variant, revert })

  const classes = useMemo(() => {
    return context ? context : mozgotekaClasses
  }, [context, mozgotekaClasses])

  const logo = useMemo(() => {
    const viewBox = {
      left: 0,
      top: 0,
      width: 100,
      height: 23,
    }
    if (safe) {
      viewBox.left = -15
      viewBox.top = -6
      viewBox.width += -viewBox.left * 2
      viewBox.height += -viewBox.top * 2
    }
    if (signet) {
      viewBox.left = 15.85
      viewBox.top = 3.85
      viewBox.height = 16.15
      viewBox.width = viewBox.height
    }
    const logo = (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox={`${viewBox.left} ${viewBox.top} ${viewBox.width} ${viewBox.height}`}
        {...rest}
      >
        <path
          className={classes.light}
          fill="currentColor"
          opacity={signet ? 0 : 1}
          d="M79.33 9.08q-.6-1.515-1.83-2.34c-.83-.56-1.83-.83-3.01-.83q-1.665 0-2.94.75c-.85.5-1.52 1.21-2.01 2.12s-.73 1.98-.73 3.2c0 1.12.25 2.12.74 2.99.5.87 1.18 1.55 2.06 2.05s1.89.74 3.04.74c1.08 0 2.08-.28 2.99-.85.66-.41 1.19-.94 1.6-1.6.24-.39.06-.9-.38-1.02l-1.53-.44a.695.695 0 0 0-.75.28c-.18.26-.4.47-.67.63-.4.24-.87.36-1.43.36-.88 0-1.55-.29-2-.86-.33-.42-.54-.97-.63-1.64h7.25c.37 0 .67-.3.68-.67.03-1.08-.13-2.04-.45-2.87m-4.72-.72c.83 0 1.42.25 1.78.75q.36.495.48 1.41h-4.95c.11-.52.3-.95.56-1.28.45-.59 1.16-.88 2.13-.88m-59.74 3.68c0-.97.63-1.78 1.49-2.08-.04-1.13-.36-2.06-.98-2.77-.66-.76-1.62-1.14-2.91-1.14-.75 0-1.43.16-2.04.49-.57.31-1.03.73-1.39 1.27-.27-.48-.63-.89-1.1-1.2-.57-.37-1.29-.56-2.17-.56-.77 0-1.47.17-2.09.51-.48.26-.87.59-1.2.99v-.56c0-.37-.3-.68-.68-.68H.68c-.37 0-.68.3-.68.68v9.88c0 .37.3.68.68.68h1.5c.37 0 .68-.3.68-.68v-6.13c0-.64.18-1.17.54-1.58s.83-.61 1.42-.61 1.1.21 1.45.62.53.99.53 1.73v5.96c0 .37.3.68.68.68h1.47c.37 0 .68-.3.68-.68v-6.13q0-.645.24-1.14c.16-.33.39-.59.68-.77s.63-.28 1.03-.28c.62 0 1.1.21 1.45.62s.53.99.53 1.73v5.96c0 .37.3.68.68.68h1.47c.37 0 .68-.3.68-.68V14.1c-.87-.3-1.5-1.12-1.5-2.09Z"
        />
        <path
          className={classes.heart}
          fill="currentColor"
          d="M27.93 12.64c.27-.26.58-.46.85-.71.14-.13.22-.22.31-.38.06-.11.09-.26.07-.38a.76.76 0 0 0-.18-.37.62.62 0 0 0-.42-.2.97.97 0 0 0-.67.22c-.03.02-.26.23-.25.24l-.1-.1c-.13-.13-.27-.25-.44-.31a.9.9 0 0 0-.59 0c-.29.1-.47.47-.4.75.08.34.35.57.62.79.29.24.59.46.83.76.02.03.04.05.06.08.1-.13.22-.25.33-.37Z"
        />
        <path
          className={
            classes.signet === 'monochrome' ? 'text-white' : 'text-blue'
          }
          fill="currentColor"
          d="M20.91 7.13c.31.27.59.61.7 1.01.12.44.01.91-.31 1.23-.5.51-1.27.63-1.91.86-.15.06-.31.13-.43.24-.03.03-.06.05-.09.08l-.02.02s0 .01-.01.02c-.01.02-.03.05-.04.07 0 .01-.02.05-.02.06 0 .02-.01.04-.02.06v.14c0 .03.02.06.03.09-.03-.08.08.13.03.07.02.03.04.05.07.07l.02.02s.01 0 .02.01c.09.06.08.05.18.07q.12.03.24.03c.22 0 .45-.02.67-.04.4-.03.83-.03 1.18.21s.53.63.44 1.05c-.07.34-.32.61-.62.77-.29.15-.59.22-.91.27-.16.02-.32.05-.47.09-.16.04-.21.05-.34.14-.05.03-.14.13-.17.2s-.04.14-.05.19v.06s.04.11.01.05c.04.09.05.1.13.16-.04-.04.06.04.05.03.03.02.07.04.1.05.09.04.28.07.42.07.75 0 1.46-.43 2.23-.34.5.06.91.31 1.28.62V6.35a1.95 1.95 0 0 0-1.95-1.95c-1 0-1.82.75-1.94 1.72.53.3 1.04.63 1.49 1.03Z"
        />
        <path
          className={
            classes.signet === 'monochrome' ? 'text-white' : 'text-green'
          }
          fill="currentColor"
          d="M22.62 15.2c-.33-.22-.68-.3-1.07-.24-.85.13-1.74.6-2.57.14-.33-.18-.57-.52-.58-.9-.02-.4.2-.77.51-1 .25-.18.56-.26.85-.31.15-.03.31-.04.46-.07.07-.01.14-.03.21-.05.04-.01.07-.02.1-.03 0 0 .11-.04.05-.02.16-.07.23-.11.33-.23-.04.06.05-.12.03-.05.03-.08.03-.1.03-.17 0 .06-.02-.06-.01-.05-.01-.02-.02-.05-.04-.07l-.05-.05-.02-.02s-.01 0-.03-.02a.6.6 0 0 0-.34-.1c-.45-.02-.89.09-1.34.03-.38-.05-.74-.22-.93-.57-.16-.29-.19-.65-.07-.96.14-.36.44-.61.78-.77.35-.17.74-.26 1.1-.39.25-.09.55-.2.73-.36.12-.11.17-.18.2-.33a.63.63 0 0 0-.04-.33c-.12-.3-.38-.54-.63-.74-.22-.18-.53-.38-.81-.55-.15-.09-.29-.18-.44-.26-1.07.01-1.94.88-1.94 1.96 0 .56.23 1.06.61 1.41-1 .09-1.79.92-1.79 1.95 0 .95.68 1.74 1.57 1.92-.24.33-.39.73-.39 1.16 0 1.08.88 1.96 1.96 1.96h.36v.36c0 1.08.88 1.96 1.96 1.96s1.96-.88 1.96-1.96v-1.62c-.22-.22-.45-.44-.71-.61Z"
        />
        <path
          className={
            classes.signet === 'monochrome' ? 'text-white' : 'text-pink'
          }
          fill="currentColor"
          d="M29.96 13.92a3.4 3.4 0 0 0-.75-.18c-.44-.05-.83 0-1.24.11.13.5.11 1.05-.06 1.52a2.5 2.5 0 0 1-1.94 1.58c-.47.07-.91.02-1.35-.17-.04-.02-.08-.04-.12-.05v.71c0 1.08.88 1.96 1.96 1.96s1.96-.88 1.96-1.96v-.35h.36c1.08 0 1.96-.88 1.96-1.96 0-.32-.08-.61-.21-.87-.14-.12-.38-.25-.57-.32Z"
        />
        <path
          className={
            classes.signet === 'monochrome' ? 'text-white' : 'text-yellow'
          }
          fill="currentColor"
          d="M30.14 10.07c.37-.36.61-.86.61-1.41 0-1.08-.88-1.96-1.96-1.96h-.36v-.36c0-1.08-.88-1.96-1.96-1.96a1.95 1.95 0 0 0-1.95 1.95v9.56c.21.14.43.26.68.33.41.11.85.04 1.22-.16a1.76 1.76 0 0 0 .92-1.83c-.06-.37-.21-.73-.45-.98-.26-.27-.57-.48-.85-.73-.3-.27-.57-.62-.65-1.03-.08-.44.06-.88.37-1.21.51-.54 1.34-.51 1.86-.06.27-.23.63-.37.98-.35.49.03.93.33 1.13.78.2.44.17.93-.09 1.34-.23.36-.58.62-.91.88-.05.04-.11.09-.16.14.86-.07 1.76.13 2.43.67.56-.34.94-.96.94-1.67 0-1.03-.79-1.87-1.79-1.95Z"
        />
        <path
          className={classes.color}
          fill="currentColor"
          opacity={signet ? 0 : 1}
          d="M25.83.28h-.78c-.54 0-.96.25-1.25.73l-.76 1.31q-.3.525-.09.93c.1.19.32.41.82.41h.46q.42 0 .72-.15c.19-.1.38-.26.56-.47l.97-1.31c.3-.35.39-.68.27-.98s-.43-.46-.91-.46ZM79.39 9.26v-.02zm-8.88 6.95.02.02zM99.74 8.34c-.31-.79-.84-1.37-1.57-1.76-.73-.38-1.59-.63-2.58-.73-1.35-.14-2.44.04-3.28.53-.62.36-1.11.83-1.48 1.41-.22.34-.06.8.31.97l1.29.56c.28.12.59.02.78-.21.21-.25.47-.43.78-.53.47-.15.91-.21 1.32-.16.74.08 1.26.29 1.54.63.22.27.33.64.34 1.1-.48.02-.94.04-1.37.06-.74.03-1.4.07-1.98.13s-1.09.14-1.52.25c-.62.16-1.13.38-1.52.67s-.69.64-.89 1.05-.33.87-.39 1.39c-.07.62.02 1.2.25 1.75.04.1.1.2.15.3s0-.01-.02-.02l-3.94-4.14 2.79-4.86a.678.678 0 0 0-.66-1.01l-1.81.19c-.22.02-.41.15-.52.34l-3.05 5.47-.89-8.43a.674.674 0 0 0-.74-.6l-1.51.16c-.37.04-.64.37-.6.74l.72 6.91c.04.25.06.52.08.79v.03l.61 5.83c.04.37.37.64.74.6l1.53-.16c.37-.04.64-.37.6-.74l-.49-4.71 4.2 4.71c.15.16.36.25.58.22l1.95-.2c.47-.05.72-.54.55-.94.22.34.51.63.87.88.52.37 1.18.59 1.98.67.97.1 1.78.02 2.45-.25.53-.21 1.04-.56 1.54-1.04l-.07.7c-.04.37.23.71.6.74l1.11.12c.37.04.71-.23.74-.6l.65-6.15c.05-.51.08-.97.08-1.39s-.09-.85-.26-1.28Zm-2.9 4.75c-.07.4-.19.75-.37 1.03-.09.15-.26.33-.49.54-.24.21-.54.39-.91.54s-.82.19-1.34.14c-.32-.03-.6-.11-.82-.23a1.2 1.2 0 0 1-.5-.48c-.11-.2-.15-.42-.12-.68.02-.19.08-.35.17-.49.1-.14.23-.27.42-.38.18-.11.42-.21.7-.29.25-.06.54-.12.86-.16.33-.04.75-.08 1.28-.12.34-.02.75-.05 1.22-.08-.03.2-.06.41-.1.66M69.73 15.4c0-.08-.03-.16-.07-.23s-.04-.07-.06-.11a.69.69 0 0 0-.66-.26c-.38.07-.74.11-1.09.12-.55.02-.97-.15-1.26-.52q-.225-.285-.3-.81c-.05-.35-.09-.74-.14-1.17l-.43-4.1 2.55-.27c.37-.04.64-.37.6-.74l-.09-.83a.674.674 0 0 0-.74-.6l-2.55.27-.26-2.43a.674.674 0 0 0-.74-.6l-1.47.15c-.37.04-.64.37-.6.74l.26 2.43-1.23.13c-.37.04-.64.37-.6.74l.09.83c0 .07.03.14.06.21h-.01c-.34-.52-.76-.97-1.27-1.35-.8-.59-1.76-.95-2.88-1.07-1.1-.12-2.11.03-3.01.44a5.3 5.3 0 0 0-2.21 1.86c-.52.75-.84 1.65-.99 2.67l-.02.04.37-3.5a.67.67 0 0 0-.6-.74l-1.11-.12a.676.676 0 0 0-.74.56c-.06-.06-.12-.13-.18-.19-.67-.59-1.54-.95-2.6-1.06-1.07-.11-2.03.04-2.87.47-.85.43-1.54 1.06-2.07 1.9s-.86 1.83-.98 2.97c-.12 1.12 0 2.14.33 3.08.07.2.16.38.24.57a.67.67 0 0 0-.65-.33l-4.58.48 4.28-8.37s0 .01.1-.2a.5.5 0 0 0-.51-.67c-.3.04-.3.03-.3.03l-7.79.81a2.2 2.2 0 0 1 .52 2.41l4.15-.43-4.42 8.6c-.08.27.11.64.46.64l8.34-.88c.37-.04.64-.37.6-.74l-.12-1.09a.4.4 0 0 0-.04-.16q.48.945 1.29 1.62c.72.6 1.59.95 2.62 1.06 1.06.11 1.97-.04 2.74-.43l-.12 1.12c-.03.42-.17.75-.42 1.01s-.57.43-.96.54c-.39.1-.79.13-1.21.09a2.4 2.4 0 0 1-1.06-.37c-.19-.12-.34-.27-.47-.44-.17-.23-.48-.3-.75-.21l-1.45.52a.68.68 0 0 0-.33 1.03q.24.33.54.63c.41.41.9.75 1.45 1.01s1.14.43 1.78.5c.82.09 1.59.06 2.32-.09.73-.14 1.36-.41 1.89-.8s.92-.92 1.18-1.58q.135-.345.21-.72.075-.36.12-.78l.75-7.18v-.02c-.07 1.05.06 2.01.41 2.87.38.94.97 1.7 1.76 2.29.8.59 1.76.95 2.88 1.07s2.13-.03 3.03-.44a5.35 5.35 0 0 0 2.22-1.87c.57-.84.92-1.83 1.04-2.99.12-1.15 0-2.2-.39-3.13-.11-.28-.25-.53-.4-.78.14.16.36.26.59.23l1.23-.13.45 4.26c.07.67.14 1.27.21 1.79.07.53.27 1.03.6 1.51.36.51.85.86 1.45 1.04.61.18 1.28.24 2.03.18.56-.05 1.13-.14 1.69-.28.32-.08.53-.39.5-.72l-.11-1.02Zm-21.74-3.3c-.07.68-.22 1.26-.44 1.75s-.53.86-.94 1.1-.92.33-1.55.27c-.61-.06-1.09-.27-1.44-.61s-.6-.78-.73-1.32a5 5 0 0 1-.1-1.74c.07-.62.22-1.18.47-1.67q.375-.735 1.02-1.14c.44-.26.97-.36 1.6-.3q.885.09 1.38.57c.33.32.55.75.67 1.27.11.53.13 1.13.06 1.81Zm10.91.03c-.1.96-.41 1.72-.91 2.28-.51.56-1.22.8-2.12.7-.88-.09-1.51-.46-1.9-1.09-.39-.64-.53-1.46-.43-2.46.07-.65.22-1.21.46-1.68s.57-.82.99-1.06c.42-.23.94-.32 1.55-.26.89.09 1.53.46 1.92 1.1s.53 1.46.43 2.45Z"
        />
      </svg>
    )
    return Component ? <Component {...rest}>{logo}</Component> : logo
  }, [
    Component,
    classes.color,
    classes.heart,
    classes.light,
    classes.signet,
    rest,
    safe,
    signet,
  ])

  return useMemo(() => {
    const content = (
      <>
        {logo}
        {children}
      </>
    )
    const Container = container ? container.as : Fragment
    const containerProps = container ? { className: container?.className } : {}
    return <Container {...containerProps}>{content}</Container>
  }, [logo, children, container])
}

export default Mozgoteka
